import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { App } from './App.tsx'
import { forPlayback } from './lib/audio-session.ts'
import './styles/base.css'
import './styles/auth.css'
import './styles/chat.css'
import './styles/sections.css'
import './styles/admin.css'

/**
 * Новая версия вступает в силу с первой перезагрузки, а не со второй.
 *
 * Обработчик уведомлений обновляется сам, но страница к этому моменту уже
 * загрузила старые файлы — и человек видит прежнюю версию, пока не обновит
 * ещё раз. Ловим момент, когда управление переходит к новому обработчику,
 * и перезагружаемся один раз.
 *
 * Защита от петли обязательна: без неё перезагрузка вызывает смену
 * управления, та — новую перезагрузку, и страница мигает бесконечно.
 */
/**
 * ВЫСОТА ПРИЛОЖЕНИЯ ПРИ ОТКРЫТОЙ КЛАВИАТУРЕ
 *
 * На телефоне клавиатура не уменьшает страницу — она её закрывает.
 * Браузер, чтобы поле ввода осталось видно, прокручивает страницу вверх;
 * приложение при этом по-прежнему считает себя во весь экран, и между
 * строкой ввода и клавиатурой остаётся полоса пустоты в те самые
 * несколько десятков точек, что занимает панель браузера над клавиатурой.
 *
 * Настоящую видимую высоту знает только `visualViewport` — по ней
 * и живём. Тогда строка ввода прижимается к клавиатуре вплотную,
 * как в привычных мессенджерах.
 */
if (window.visualViewport) {
  const viewport = window.visualViewport

  const fit = () => {
    document.documentElement.style.setProperty('--app-height', `${viewport.height}px`)

    /**
     * Отменяем прокрутку, которую браузер сделал сам.
     *
     * Он увёл страницу вверх, чтобы показать поле ввода. После того как
     * приложение ужалось до видимой высоты, прятать больше нечего,
     * а сдвиг остаётся — и шапка переписки уезжает за край экрана.
     */
    if (window.scrollY !== 0) window.scrollTo(0, 0)
  }

  viewport.addEventListener('resize', fit)
  viewport.addEventListener('scroll', fit)
  // Поворот экрана и появление адресной строки меняют высоту, но
  // событие о видимой части приходит не во всех браузерах
  window.addEventListener('resize', fit)
  window.addEventListener('orientationchange', fit)
  fit()
}

/**
 * ДОИГРАЛО — НАЧНИ СНАЧАЛА
 *
 * Голосовые и кружочки с телефона пишутся потоком: длительности в файле
 * нет, браузер узнаёт её, только доиграв до конца. После этого повторное
 * нажатие «играть» начинает с конца и тут же заканчивается — второй раз
 * запись молчит. Встроенные браузерные проигрыватели (кружочки, видео)
 * перезагружаем здесь, для всех разом.
 * Событие «доиграло» не всплывает, ловим его на пути вниз.
 */
document.addEventListener(
  'ended',
  (event) => {
    const media = event.target
    if (!(media instanceof HTMLMediaElement)) return
    // Свои проигрыватели (голосовые) решают это сами
    if (!media.controls) return
    // Не перемотка, а перезагрузка: Safari такие файлы перематывать не умеет
    media.load()
  },
  true,
)

/**
 * Кружочки и видео играют встроенным проигрывателем — режим звука для
 * них задаём здесь, в момент запуска. Звук звонка не трогаем: он идёт
 * из живого потока, а не из файла.
 */
document.addEventListener(
  'play',
  (event) => {
    const media = event.target
    if (media instanceof HTMLMediaElement && !media.srcObject) forPlayback()
  },
  true,
)

if ('serviceWorker' in navigator) {
  let reloading = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return
    reloading = true
    location.reload()
  })

  /**
   * Сами спрашиваем, не вышла ли новая версия.
   *
   * Браузер проверяет это при переходе по адресу — а установленное
   * приложение не переходит никуда: его открывают с домашнего экрана,
   * и оно может неделю жить на сборке, которую однажды положило в кеш.
   * Человек при этом видит старое приложение и считает, что починки
   * не случилось.
   *
   * Спрашиваем при запуске и каждый раз, когда приложение возвращается
   * на экран, но не чаще раза в четверть часа: проверка стоит запроса
   * к сети, а мобильный интернет бывает дорог.
   */
  const ASK_EVERY = 15 * 60 * 1000
  let askedAt = 0

  const askForUpdate = () => {
    if (document.visibilityState !== 'visible') return
    if (Date.now() - askedAt < ASK_EVERY) return
    askedAt = Date.now()
    void navigator.serviceWorker.getRegistration().then((reg) => reg?.update())
  }

  window.addEventListener('load', askForUpdate)
  document.addEventListener('visibilitychange', askForUpdate)
}


const container = document.getElementById('root')
if (!container) throw new Error('Не найден корневой элемент')

createRoot(container).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
)
